/**
 * Сбор технического контекста для баг-трекера.
 *
 * Модуль предоставляет:
 *  - getOrCreateSessionId() — стабильный UUID сессии в localStorage
 *  - installErrorHandlers() — глобальные перехватчики JS и fetch-ошибок
 *  - getConsoleErrors() / getNetworkErrors() — снимок ring-буфера ошибок
 *  - detectDeviceType() — парсинг UA в mobile/tablet/desktop
 *  - attachAxiosInterceptor(axios) — опциональный перехватчик axios
 *
 * Безопасность: буферы хранятся в module-level переменных (не в localStorage),
 * чтобы не расти неограниченно.
 */

const SESSION_KEY = 'bug_session_id';

const MAX_CONSOLE_ERRORS = 10;
const MAX_NETWORK_ERRORS = 5;

// --- Ring-буферы ошибок ---
type ConsoleErrorItem = {
  ts: string;
  message: string;
  source?: string;
  lineno?: number;
  colno?: number;
  stack?: string;
};

type NetworkErrorItem = {
  ts: string;
  url: string;
  method: string;
  status?: number;
  statusText?: string;
};

const consoleBuffer: ConsoleErrorItem[] = [];
const networkBuffer: NetworkErrorItem[] = [];

let handlersInstalled = false;

function push<T>(buffer: T[], item: T, max: number): void {
  buffer.push(item);
  if (buffer.length > max) buffer.shift();
}

/**
 * Возвращает стабильный UUID сессии.
 * Создаётся при первом вызове, сохраняется в localStorage.
 */
export function getOrCreateSessionId(): string {
  if (typeof window === 'undefined') return '';
  try {
    let sid = localStorage.getItem(SESSION_KEY);
    if (!sid) {
      sid = typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `sid-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem(SESSION_KEY, sid);
    }
    return sid;
  } catch {
    return '';
  }
}

/**
 * Устанавливает глобальные перехватчики ошибок.
 * Безопасно вызывать несколько раз — установка произойдёт только один раз.
 */
export function installErrorHandlers(): void {
  if (typeof window === 'undefined' || handlersInstalled) return;
  handlersInstalled = true;

  // JS-ошибки в коде
  window.addEventListener('error', (ev) => {
    try {
      // Фильтруем ошибки не из нашего origin (Метрика, сторонние iframe)
      if (ev.filename && !ev.filename.startsWith(window.location.origin)
          && !ev.filename.startsWith('/')) {
        return;
      }
      push(consoleBuffer, {
        ts: new Date().toISOString(),
        message: String(ev.message || '').slice(0, 500),
        source: ev.filename,
        lineno: ev.lineno,
        colno: ev.colno,
        stack: ev.error?.stack ? String(ev.error.stack).slice(0, 2000) : undefined,
      }, MAX_CONSOLE_ERRORS);
    } catch { /* silent */ }
  });

  // Unhandled promise rejection
  window.addEventListener('unhandledrejection', (ev) => {
    try {
      const reason = ev.reason;
      const message = reason?.message || String(reason || 'unhandledrejection');
      push(consoleBuffer, {
        ts: new Date().toISOString(),
        message: message.slice(0, 500),
        stack: reason?.stack ? String(reason.stack).slice(0, 2000) : undefined,
      }, MAX_CONSOLE_ERRORS);
    } catch { /* silent */ }
  });

  // Monkey-patch fetch — логируем 4xx/5xx
  try {
    const origFetch = window.fetch.bind(window);
    window.fetch = async (input: any, init?: RequestInit): Promise<Response> => {
      const method = (init?.method || (typeof input === 'object' && input?.method) || 'GET').toUpperCase();
      const url = typeof input === 'string' ? input : (input?.url || String(input));
      try {
        const res = await origFetch(input, init);
        if (!res.ok) {
          push(networkBuffer, {
            ts: new Date().toISOString(),
            url: String(url).slice(0, 500),
            method,
            status: res.status,
            statusText: res.statusText,
          }, MAX_NETWORK_ERRORS);
        }
        return res;
      } catch (err: any) {
        push(networkBuffer, {
          ts: new Date().toISOString(),
          url: String(url).slice(0, 500),
          method,
          statusText: err?.message || 'network error',
        }, MAX_NETWORK_ERRORS);
        throw err;
      }
    };
  } catch { /* silent */ }
}

/**
 * Перехватчик для axios-инстанса (опционально).
 * Используется в admin (там axios-клиент).
 */
export function attachAxiosInterceptor(axiosInstance: any): void {
  if (!axiosInstance?.interceptors?.response) return;
  axiosInstance.interceptors.response.use(
    (response: any) => response,
    (error: any) => {
      try {
        const cfg = error?.config || {};
        const status = error?.response?.status;
        if (!status || status >= 400) {
          push(networkBuffer, {
            ts: new Date().toISOString(),
            url: String(cfg.url || '').slice(0, 500),
            method: String(cfg.method || 'GET').toUpperCase(),
            status,
            statusText: error?.response?.statusText || error?.message,
          }, MAX_NETWORK_ERRORS);
        }
      } catch { /* silent */ }
      return Promise.reject(error);
    },
  );
}

export function getConsoleErrors(): ConsoleErrorItem[] {
  return [...consoleBuffer];
}

export function getNetworkErrors(): NetworkErrorItem[] {
  return [...networkBuffer];
}

/**
 * Определяет тип устройства по User Agent.
 */
export function detectDeviceType(): 'mobile' | 'tablet' | 'desktop' {
  if (typeof navigator === 'undefined') return 'desktop';
  const ua = navigator.userAgent || '';
  if (/iPad|Tablet|PlayBook|Silk/i.test(ua)) return 'tablet';
  if (/Mobi|Android|iPhone|iPod|Windows Phone/i.test(ua)) return 'mobile';
  return 'desktop';
}

/**
 * Собирает полный технический контекст браузера.
 */
export function collectBrowserContext() {
  if (typeof window === 'undefined') return {};
  const w = window;
  const nav = typeof navigator !== 'undefined' ? navigator : undefined;
  return {
    page_url: w.location.href,
    route_name: w.location.pathname,
    referrer: typeof document !== 'undefined' ? document.referrer || undefined : undefined,
    user_agent: nav?.userAgent,
    screen_size: typeof screen !== 'undefined' ? `${screen.width}x${screen.height}` : undefined,
    viewport_size: `${w.innerWidth}x${w.innerHeight}`,
    device_type: detectDeviceType(),
    locale: nav?.language,
    session_id: getOrCreateSessionId(),
    console_errors: getConsoleErrors(),
    network_errors: getNetworkErrors(),
  };
}
